type TranslationDetail = {
  _text: string;
  /** optional description to send to openai to help with translating the text **/
  _comment?: string;
  /**
   * if this is a `mixology` translation, use this flag to prevent this translation from being used as the
   * canonical translation for all usages of this phrase in other translations. (i.e. setting this to true will
   * prevent this translation being added to the mixology dictionary that's used for translating other phrases).
   */
  excludeFromMixologyDict?: boolean;
};

export type TranslationEntry = string | TranslationDetail;
export type FlatDictionary = Record<string, TranslationEntry>;

/**
 * Adds context to a translation for the AI.
 */
export function withComment<T extends string>(comment: string, text: T): T {
  return withDetail(text, {_comment: comment});
}

export function withDetail<T extends string>(text: T, detail: Partial<TranslationDetail>): T {
  const newValue: TranslationDetail = {
    _text: text,
    ...detail,
  };

  // @ts-ignore -- the cast is clearly wrong: we're doing this so i18next types can do all the inference it needs as if this were a normal string
  // the object value gets bypassed during runtime
  return newValue as T;
}

/**
 * Checks if a value is a translation with a comment.
 */
export function isTranslationDetail(entry: TranslationEntry): entry is TranslationDetail {
  return typeof entry === `object` && `_text` in entry;
}

export function getTranslationText(entry: TranslationEntry): string {
  return isTranslationDetail(entry) ? entry._text : entry;
}

/**
 * Flattens an input dictionary.
 */
export function getFlattenedDictionary(dictionary: Record<string, any>, includeDetail: boolean): FlatDictionary {
  const flattened: FlatDictionary = {};

  const iterate = (obj: Record<string, any>, path: string[] = []) => {
    for (const key in obj) {
      let value = obj[key];
      const newPath = [...path, key];
      const hasDetail = isTranslationDetail(value);

      // If the value is a translation with a comment, we want to retain the comment for translation
      if (typeof value === `string` || hasDetail) {
        if (hasDetail && !includeDetail) {
          value = value._text;
        }

        flattened[newPath.join(`.`)] = value;
      } else {
        iterate(value, newPath);
      }
    }
  };

  iterate(dictionary);

  return flattened;
}
